import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { Notification } from "../types/notifications";
import { getAuthHeader } from "../utils/authHeader";

export interface NotificationsState {
    list: Notification[];
    unreadCount: number;
    loading: boolean;
}

const initialState: NotificationsState = {
    list: [],
    unreadCount: 0,
    loading: false,
};

export const fetchNotifications = createAsyncThunk(
    "notifications/fetch",
    async (params: { page: number; limit: number }) => {
        const res = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/notifications?page=${params.page}&limit=${params.limit}`,
            {
                headers: await getAuthHeader(),
            }
        );
        return res.data as Notification[];
    }
);

export const fetchUnreadCount = createAsyncThunk(
    "notifications/fetchUnreadCount",
    async () => {
        const res = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/notifications/unread-count`,
            {
                headers: await getAuthHeader(),
            }
        );
        return res.data.count as number;
    }
);

export const markNotificationRead = createAsyncThunk(
    "notifications/markRead",
    async (id: string) => {
        await axios.patch(
            `${process.env.REACT_APP_BACKEND_URL}/notifications/${id}/read`,
            {},
            {
                headers: await getAuthHeader(),
            }
        );
        return id;
    }
);

export const markAllRead = createAsyncThunk(
    "notifications/markAllRead",
    async () => {
        await axios.patch(
            `${process.env.REACT_APP_BACKEND_URL}/notifications/read-all`,
            {},
            {
                headers: await getAuthHeader(),
            }
        );
    }
);

export const removeNotification = createAsyncThunk(
    "notifications/remove",
    async (id: string) => {
        await axios.delete(
            `${process.env.REACT_APP_BACKEND_URL}/notifications/${id}`,
            {
                headers: await getAuthHeader(),
            }
        );
        return id;
    }
);

const notificationsSlice = createSlice({
    name: "notifications",
    initialState,
    reducers: {
        addNotification(state, action: PayloadAction<Notification>) {
            // Guard against the same notification arriving twice (a socket re-delivery, or a
            // brief two-socket overlap when impersonation switches). A duplicate in the list
            // both renders twice and inflates any unread tally derived from it.
            if (state.list.some((n) => n._id === action.payload._id)) {
                return;
            }
            state.list.unshift(action.payload);
        },
        setNotifications(state, action: PayloadAction<Notification[]>) {
            state.list = action.payload;
        },
        removeNotificationLocal(state, action: PayloadAction<string>) {
            const id = action.payload;
            // Decide wasUnread BEFORE filtering — checking after the item is gone always
            // reads false, so the count would never decrement.
            const wasUnread = state.list.some(
                (n) => n._id === id && !n.isRead
            );
            state.list = state.list.filter((n) => n._id !== id);
            if (wasUnread) {
                state.unreadCount = Math.max(0, state.unreadCount - 1);
            }
        },
        removeExpired(state) {
            const now = Date.now();
            // Only adjust the badge by the unread items we actually drop. Recomputing it from
            // the list is wrong: the list holds one page (+ any socket-added items), so deriving
            // the count from it under-counts unread beyond the page and over-counts on any
            // duplicate — unreadCount is server-authoritative (see fetchNotifications.fulfilled).
            const expiredUnread = state.list.filter(
                (n) =>
                    n.expiryDate &&
                    new Date(n.expiryDate).getTime() <= now &&
                    !n.isRead
            ).length;
            state.list = state.list.filter(
                (n) => !n.expiryDate || new Date(n.expiryDate).getTime() > now
            );
            state.unreadCount = Math.max(0, state.unreadCount - expiredUnread);
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchNotifications.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchNotifications.fulfilled, (state, action) => {
                const now = Date.now();
                const valid = action.payload.filter(
                    (n) =>
                        !n.expiryDate || new Date(n.expiryDate).getTime() > now
                );
                state.list = valid;
                // unreadCount is NOT derived from the paginated list — it is managed
                // exclusively by fetchUnreadCount, markNotificationRead, and markAllRead.
                // This prevents a partial page fetch from silently resetting the badge.
                state.loading = false;
            })
            .addCase(fetchUnreadCount.fulfilled, (state, action) => {
                state.unreadCount = action.payload;
            })
            .addCase(markNotificationRead.fulfilled, (state, action) => {
                const idx = state.list.findIndex(
                    (n) => n._id === action.payload
                );
                if (idx !== -1) state.list[idx].isRead = true;
                state.unreadCount = Math.max(0, state.unreadCount - 1);
            })
            .addCase(markAllRead.fulfilled, (state) => {
                state.list = state.list.map((n) => ({ ...n, isRead: true }));
                state.unreadCount = 0;
            });
    },
});

export const {
    addNotification,
    setNotifications,
    removeNotificationLocal,
    removeExpired,
} = notificationsSlice.actions;
export default notificationsSlice.reducer;
